import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gamePlayService } from '../../services/db';
import Spinner from '../../components/ui/Spinner';
import {
  CalendarIcon,
  TrophyIcon,
  PlayIcon,
  InfoIcon,
  ClockIcon,
  GlobeLanguageIcon,
  LocationPinIcon,
  FolderIcon,
  CheckCircleIcon,
  LinkIcon,
  BooksIcon,
  WarningIcon,
} from '../../components/common/Icons';

const formatTimestamp = (val) => {
  if (!val) return '—';
  try {
    let date;
    if (val.toDate && typeof val.toDate === 'function') {
      date = val.toDate();
    } else if (val.seconds) {
      date = new Date(val.seconds * 1000);
    } else if (val instanceof Date) {
      date = val;
    } else {
      date = new Date(val);
    }
    if (isNaN(date.getTime())) return '—';

    const pad = (n) => String(n).padStart(2, '0');
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  } catch {
    return '—';
  }
};

const SeasonLobbyPage = () => {
  const { seasonId } = useParams();
  const navigate = useNavigate();

  const [season, setSeason] = useState(null);
  const [quizzes, setQuizzes] = useState({ ongoing: [], upcoming: [], finished: [], all: [] });
  const [leaderboardViewData, setLeaderboardViewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('quizzes'); // 'quizzes' | 'completed'
  const [showAboutModal, setShowAboutModal] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      setLoading(true);
      try {
        const [seasonData, quizData, lbSettings] = await Promise.all([
          gamePlayService.getSeasonDetails(seasonId),
          gamePlayService.getPartitionedQuizzes(seasonId, 30),
          gamePlayService.getSeasonLeaderboardSetting(seasonId),
        ]);

        if (isMounted) {
          setSeason(seasonData || null);
          setQuizzes(quizData || { ongoing: [], upcoming: [], finished: [], all: [] });
          setLeaderboardViewData(lbSettings || { season_leaderboard_available: true });
        }
      } catch (err) {
        console.error('Failed to load season data:', err);
        if (isMounted) {
          setSeason(null);
          setQuizzes({ ongoing: [], upcoming: [], finished: [], all: [] });
          setLeaderboardViewData({ season_leaderboard_available: true });
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (seasonId) loadData();

    return () => {
      isMounted = false;
    };
  }, [seasonId]);

  if (loading) {
    return (
      <div style={{ backgroundColor: '#FBF6EF', minHeight: '90vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (!season) {
    return (
      <div style={{ backgroundColor: '#FBF6EF', minHeight: '90vh', padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
          <WarningIcon size={44} style={{ color: '#D97706' }} />
        </div>
        <h2 style={{ fontSize: '22px', fontWeight: 'bold', color: '#332E2A' }}>Season Not Found</h2>
        <p style={{ color: 'rgba(51, 46, 42, 0.7)', marginBottom: '20px' }}>
          This contest tournament may have expired or is not currently active.
        </p>
        <button
          type="button"
          onClick={() => navigate('/play')}
          style={{
            backgroundColor: '#FF5C56',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '100px',
            padding: '12px 24px',
            fontWeight: 'bold',
            cursor: 'pointer',
          }}
        >
          ← Back to Tournaments
        </button>
      </div>
    );
  }

  const isCompleted = (season.status || '').toLowerCase() === 'completed';
  const isSingleContest = (season.type || '').toLowerCase() === 'single contest';
  const isLeaderboardAvailable = leaderboardViewData ? leaderboardViewData.season_leaderboard_available !== false : true;

  const initials = season.season ? season.season[0].toUpperCase() : 'S';

  const handleShareSeason = () => {
    const text = `🕊️ Join me in studying the Word! We are playing the "${season.season}" series on Bible Contest. Join here: https://go.biblecontest.app/season/${seasonId}`;
    if (navigator.share) {
      navigator.share({ title: season.season, text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      alert('Season link copied to clipboard!');
    }
  };

  return (
    <div style={{ backgroundColor: '#FBF6EF', minHeight: '90vh', padding: '16px 12px 80px', position: 'relative' }}>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        {/* Header matching SliverAppBar in SeasonHome.dart */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: '#FBF6EF',
            padding: '8px 0',
          }}
        >
          <button
            type="button"
            className="page-header__back"
            onClick={() => navigate('/play')}
            aria-label="Back to tournaments"
          >
            ←
          </button>

          <h1
            style={{
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#332E2A',
              margin: 0,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              textAlign: 'center',
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              padding: '0 12px',
            }}
          >
            {season.season}
          </h1>

          <div style={{ width: '40px' }} aria-hidden="true" />
        </div>

        {/* Tab Bar matching _SliverTabBarDelegate in SeasonHome.dart */}
        {!isCompleted && !isSingleContest && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '20px',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                backgroundColor: '#EDE8E1',
                borderRadius: '100px',
                padding: '4px',
                gap: '4px',
                width: '100%',
                maxWidth: '360px',
              }}
            >
              <button
                type="button"
                onClick={() => setActiveTab('quizzes')}
                style={{
                  flex: 1,
                  border: 'none',
                  background: activeTab === 'quizzes' ? '#C08552' : 'transparent',
                  color: activeTab === 'quizzes' ? '#FFFFFF' : '#332E2A',
                  fontWeight: activeTab === 'quizzes' ? 'bold' : '600',
                  fontSize: '14px',
                  padding: '9px 16px',
                  borderRadius: '100px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  textAlign: 'center',
                }}
              >
                Quizzes
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('completed')}
                style={{
                  flex: 1,
                  border: 'none',
                  background: activeTab === 'completed' ? '#C08552' : 'transparent',
                  color: activeTab === 'completed' ? '#FFFFFF' : '#332E2A',
                  fontWeight: activeTab === 'completed' ? 'bold' : '600',
                  fontSize: '14px',
                  padding: '9px 16px',
                  borderRadius: '100px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  textAlign: 'center',
                }}
              >
                Completed
              </button>
            </div>
          </div>
        )}

        {/* Main List matching SeasonQuizzesTab in SeasonHome.dart */}
        {isSingleContest ? (
          quizzes.all.length === 0 ? (
            <EmptyState message="No quizzes currently available for this contest." />
          ) : (
            <QuizListSection quizzes={quizzes.all} seasonId={seasonId} />
          )
        ) : isCompleted || activeTab === 'completed' ? (
          <div>
            {quizzes.finished.length === 0 ? (
              <EmptyState message="No completed quizzes yet in this season." />
            ) : (
              <QuizListSection quizzes={quizzes.finished} seasonId={seasonId} />
            )}
          </div>
        ) : (
          /* Active Quizzes Tab */
          <div>
            {quizzes.ongoing.length === 0 && quizzes.upcoming.length === 0 ? (
              <EmptyState message="No active or upcoming rounds currently available. Check back soon!" />
            ) : (
              <>
                {quizzes.ongoing.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <div
                      style={{
                        fontSize: '15px',
                        fontWeight: 'bold',
                        color: '#332E2A',
                        marginBottom: '10px',
                        letterSpacing: '0.02em',
                      }}
                    >
                      Ongoing Quizzes
                    </div>
                    <QuizListSection quizzes={quizzes.ongoing} seasonId={seasonId} />
                  </div>
                )}

                {quizzes.upcoming.length > 0 && (
                  <div>
                    <div
                      style={{
                        fontSize: '15px',
                        fontWeight: 'bold',
                        color: '#332E2A',
                        marginBottom: '10px',
                        letterSpacing: '0.02em',
                      }}
                    >
                      Upcoming Quizzes
                    </div>
                    <QuizListSection quizzes={quizzes.upcoming} seasonId={seasonId} />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Floating Action Buttons positioned above Support Chat to prevent overlap */}
        <div className="season-fab-container">
          <button
            type="button"
            className="season-fab-btn season-fab-btn--info"
            onClick={() => setShowAboutModal(true)}
            title="Season Information"
            aria-label="Season Information"
          >
            <InfoIcon size={20} style={{ color: '#FFFFFF' }} />
          </button>

          {!isSingleContest && isLeaderboardAvailable && (
            <button
              type="button"
              className="season-fab-btn season-fab-btn--leaderboard"
              onClick={() => navigate(`/play/season/${seasonId}/leaderboard`)}
              title="Season Leaderboard"
              aria-label="Season Leaderboard"
            >
              <TrophyIcon size={24} style={{ color: '#FFFFFF' }} />
            </button>
          )}
        </div>

        {/* About Season Modal matching _showAboutBottomSheet in SeasonHome.dart */}
        {showAboutModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              zIndex: 100,
              padding: '16px',
            }}
            onClick={() => setShowAboutModal(false)}
          >
            <div
              style={{
                backgroundColor: '#FBF6EF',
                borderRadius: '24px 24px 16px 16px',
                width: '100%',
                maxWidth: '540px',
                padding: '24px',
                maxHeight: '85vh',
                overflowY: 'auto',
                boxShadow: '0 -8px 24px rgba(0, 0, 0, 0.15)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag Handle Bar */}
              <div
                style={{
                  width: '40px',
                  height: '4px',
                  backgroundColor: '#E2DCD5',
                  borderRadius: '2px',
                  margin: '0 auto 20px',
                }}
              />

              {/* Season Header Row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '12px',
                    backgroundColor: '#F1ECE6',
                    border: '1.5px solid #E2DCD5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '22px',
                    fontWeight: 'bold',
                    color: '#332E2A',
                    flexShrink: 0,
                  }}
                >
                  {season.logo ? (
                    <img
                      src={season.logo}
                      alt={season.season}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '10px' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    initials
                  )}
                </div>

                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: '0 0 4px', color: '#332E2A' }}>
                    {season.season}
                  </h3>
                  {season.host && (
                    <div style={{ fontSize: '13px', color: 'rgba(51, 46, 42, 0.65)', fontWeight: 500 }}>
                      Hosted by {season.host}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ height: '1px', backgroundColor: '#E2DCD5', margin: '16px 0' }} />

              {/* Description */}
              {season.discription || season.description ? (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#332E2A', marginBottom: '6px' }}>
                    About Season
                  </div>
                  <p style={{ fontSize: '13.5px', color: 'rgba(51, 46, 42, 0.8)', lineHeight: 1.5, margin: 0 }}>
                    {season.discription || season.description}
                  </p>
                </div>
              ) : null}

              {/* Information Chips */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#332E2A', marginBottom: '10px' }}>
                  Information
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <MetaChip
                    icon={<CheckCircleIcon size={13} style={{ color: '#10B981' }} />}
                    text={season.status === 'active' ? 'Active' : 'Completed'}
                    highlight
                  />
                  {season.type && (
                    <MetaChip
                      icon={<FolderIcon size={13} style={{ color: '#C08552' }} />}
                      text={season.type}
                    />
                  )}
                  {season.country && (
                    <MetaChip
                      icon={<LocationPinIcon size={13} style={{ color: '#C08552' }} />}
                      text={season.country}
                    />
                  )}
                  {season.timezone && (
                    <MetaChip
                      icon={<GlobeLanguageIcon size={13} style={{ color: '#6366F1' }} />}
                      text={season.timezone}
                    />
                  )}
                  {season.frequency && (
                    <MetaChip
                      icon={<ClockIcon size={13} style={{ color: '#C08552' }} />}
                      text={season.frequency}
                    />
                  )}
                  {season.languages && (
                    <MetaChip
                      icon={<GlobeLanguageIcon size={13} style={{ color: '#C08552' }} />}
                      text={Array.isArray(season.languages) ? season.languages.join(', ') : season.languages}
                    />
                  )}
                </div>
              </div>

              {/* Share Season Button */}
              <button
                type="button"
                onClick={handleShareSeason}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  borderRadius: '12px',
                  border: '1.5px solid #E2DCD5',
                  backgroundColor: '#FFFFFF',
                  color: '#332E2A',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <LinkIcon size={16} style={{ color: 'var(--clr-primary)' }} /> Share Season
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const QuizListSection = ({ quizzes, seasonId }) => {
  const navigate = useNavigate();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {quizzes.map((quiz) => {
        const quizId = quiz.quiz_id || quiz.id;
        const status = (quiz.status || 'Ongoing').toLowerCase();

        let accentColor = '#FF5C56';
        let statusIcon = '▶️';
        let dateLabel = 'Ends';
        let dateValue = formatTimestamp(quiz.end || quiz.endDate);

        if (status === 'ongoing') {
          accentColor = '#FF5C56';
          statusIcon = <PlayIcon size={20} style={{ color: accentColor }} />;
          dateLabel = 'Ends';
          dateValue = formatTimestamp(quiz.end || quiz.endDate);
        } else if (status === 'finished') {
          accentColor = '#8A827C';
          statusIcon = <TrophyIcon size={20} style={{ color: accentColor }} />;
          dateLabel = 'Completed';
          dateValue = formatTimestamp(quiz.end || quiz.endDate);
        } else {
          accentColor = '#C08552';
          statusIcon = <CalendarIcon size={20} style={{ color: accentColor }} />;
          dateLabel = 'Starts';
          dateValue = formatTimestamp(quiz.start || quiz.startDate);
        }

        return (
          <div
            key={quizId}
            onClick={() => navigate(`/play/season/${seasonId}/quiz/${quizId}`)}
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid rgba(51, 46, 42, 0.08)',
              padding: '14px 16px',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
          >
            {/* Accent Icon Container */}
            <div
              style={{
                width: '46px',
                height: '46px',
                borderRadius: '12px',
                backgroundColor: `${accentColor}18`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                marginRight: '14px',
                flexShrink: 0,
              }}
            >
              {statusIcon}
            </div>

            {/* Quiz Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: '15px',
                  fontWeight: 'bold',
                  color: '#332E2A',
                  marginBottom: '4px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {quiz.title}
              </div>

              <div
                style={{
                  fontSize: '12px',
                  color: 'rgba(51, 46, 42, 0.65)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '4px',
                }}
              >
                {quiz.language && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <GlobeLanguageIcon size={12} style={{ color: 'rgba(51, 46, 42, 0.65)' }} /> {quiz.language}
                  </span>
                )}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <ClockIcon size={12} style={{ color: 'rgba(51, 46, 42, 0.65)' }} /> {quiz.duration || 5} mins
                </span>
              </div>

              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: '#332E2A',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <CalendarIcon size={14} style={{ color: accentColor, flexShrink: 0 }} />
                <span>
                  {dateLabel}: {dateValue}
                </span>
              </div>
            </div>

            {/* Status Badge (if ongoing) */}
            {status === 'ongoing' && (
              <div
                style={{
                  marginLeft: '10px',
                  backgroundColor: 'rgba(255, 92, 86, 0.12)',
                  color: '#FF5C56',
                  borderRadius: '12px',
                  padding: '4px 8px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <PlayIcon size={10} style={{ color: '#FF5C56' }} /> Ongoing
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const MetaChip = ({ icon, text, highlight }) => (
  <div
    style={{
      backgroundColor: highlight ? 'rgba(255, 92, 86, 0.1)' : '#FFFFFF',
      border: `1px solid ${highlight ? 'rgba(255, 92, 86, 0.3)' : '#E2DCD5'}`,
      borderRadius: '8px',
      padding: '5px 10px',
      fontSize: '12px',
      fontWeight: highlight ? 'bold' : 500,
      color: highlight ? '#FF5C56' : '#332E2A',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
    }}
  >
    <span>{icon}</span>
    <span>{text}</span>
  </div>
);

const EmptyState = ({ message }) => (
  <div
    style={{
      textAlign: 'center',
      padding: '48px 20px',
      backgroundColor: '#FFFFFF',
      borderRadius: '16px',
      border: '1px solid #E2DCD5',
      color: 'rgba(51, 46, 42, 0.6)',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
      <div style={{ width: '54px', height: '54px', borderRadius: '50%', background: 'rgba(192, 133, 82, 0.1)', color: '#C08552', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <BooksIcon size={28} />
      </div>
    </div>
    <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>{message}</p>
  </div>
);

export default SeasonLobbyPage;
